import { api } from '../api/client';
import { userMeetsPlan } from '../components/PlanGate';

// ── WMO weather code → plain English ─────────────────────────────────────────
const WMO_CODES = {
  0: 'Clear skies',
  1: 'Mainly clear', 2: 'Partly cloudy', 3: 'Overcast',
  45: 'Fog', 48: 'Icy fog',
  51: 'Light drizzle', 53: 'Drizzle', 55: 'Heavy drizzle',
  61: 'Light rain', 63: 'Moderate rain', 65: 'Heavy rain',
  71: 'Light snow', 73: 'Moderate snow', 75: 'Heavy snow',
  80: 'Rain showers', 81: 'Heavy showers', 82: 'Violent showers',
  95: 'Thunderstorms',
};

/**
 * Fetch pending email-detected event proposals for the active workspace.
 * Workspace-scoped server-side; returns [] on any failure.
 */
export async function fetchEventProposals() {
  try {
    const res = await api('/api/event-proposals');
    if (!res?.ok) return [];
    const rows = await res.json();
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

/**
 * Fetch weather from Open-Meteo (free, no API key required).
 * Returns a single plain-English sentence or null if unavailable.
 */
export async function fetchWeather(lat, lon, timezone = 'auto') {
  try {
    const url =
      `https://api.open-meteo.com/v1/forecast` +
      `?latitude=${lat}&longitude=${lon}` +
      `&current_weather=true&daily=precipitation_sum` +
      `&timezone=${encodeURIComponent(timezone)}&forecast_days=1`;

    const res = await fetch(url);
    if (!res.ok) return null;

    const data = await res.json();
    const cw   = data.current_weather;
    const rain = data.daily?.precipitation_sum?.[0] ?? 0;
    const desc = WMO_CODES[cw?.weathercode] || 'Variable weather';
    const temp = Math.round(cw?.temperature ?? 0);

    let text = `${desc}, ${temp}°C today.`;
    if (rain > 2) text += ' Rain expected — worth planning ahead if you\'re travelling.';

    return text;
  } catch {
    return null;
  }
}

/**
 * Return today's meetings from all calendar sources, time-sorted.
 */
export function getTodaysMeetings(tasks, googleEvents = [], icalEvents = [], outlookEvents = []) {
  const todayStr = new Date().toDateString();

  const calendarEvents = [
    ...googleEvents,
    ...icalEvents,
    ...outlookEvents,
  ].map(e => ({
    time: e.time || formatTime(e.start || e.datetime),
    title: e.summary || e.title || e.subject || 'Event',
    attendees: e.attendees || [],
    location: e.location || e.meetingLink || '',
    _dt: new Date(e.start || e.datetime || e.date),
  })).filter(e => !isNaN(e._dt) && e._dt.toDateString() === todayStr);

  const meetingTasks = tasks
    .filter(t => !t.completed && t.taskType === 'meeting')
    .filter(t => {
      const dt = new Date(t.datetime);
      return !isNaN(dt) && dt.toDateString() === todayStr;
    })
    .map(t => ({
      time: formatTime(t.datetime),
      title: t.title,
      attendees: t.assignedTeam || [],
      location: t.location || t.meetingLink || '',
      _dt: new Date(t.datetime),
    }));

  return [...calendarEvents, ...meetingTasks]
    .sort((a, b) => a._dt - b._dt)
    .map(({ _dt, ...rest }) => rest);
}

/**
 * Return top 5 priority tasks due today or overdue (non-meeting only).
 */
export function getPriorityTasks(tasks) {
  const now = new Date();
  const todayStr = now.toDateString();

  const PRIORITY_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3 };

  return tasks
    .filter(t => {
      if (t.completed || t.taskType === 'meeting') return false;
      const dt = new Date(t.deadline || t.datetime);
      if (isNaN(dt)) return false;
      return dt.toDateString() === todayStr || dt < now;
    })
    .sort((a, b) => {
      const aDt = new Date(a.deadline || a.datetime);
      const bDt = new Date(b.deadline || b.datetime);
      const aOver = aDt < now;
      const bOver = bDt < now;
      if (aOver !== bOver) return aOver ? -1 : 1;
      const pDiff = (PRIORITY_ORDER[a.priority] ?? 2) - (PRIORITY_ORDER[b.priority] ?? 2);
      return pDiff !== 0 ? pDiff : aDt - bDt;
    })
    .slice(0, 5)
    .map(t => {
      const dt = new Date(t.deadline || t.datetime);
      const daysOverdue = dt < now ? Math.ceil((now - dt) / 86400000) : 0;
      return { title: t.title, priority: t.priority || 'Medium', daysOverdue };
    });
}

/**
 * Return overdue or due-today invoices.
 */
export function getInvoiceAlerts(invoices = []) {
  if (!invoices.length) return [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return invoices
    .filter(inv => inv.status !== 'paid' && inv.dueDate)
    .map(inv => {
      const due = new Date(inv.dueDate);
      due.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((today - due) / 86400000);
      if (diffDays < 0) return null; // not yet due
      return {
        clientName:  inv.contactName || 'Unknown client',
        amount:      inv.amount || 0,
        currency:    inv.currency || '£',
        daysOverdue: diffDays,
        dueToday:    diffDays === 0,
      };
    })
    .filter(Boolean);
}

/**
 * Return team members who haven't responded to an assigned task in 48+ hours.
 * Only for Team/Enterprise users. Members are read from the workspace-scoped
 * /api/team/members endpoint (never the legacy userManager mock), so the roster
 * belongs to the active (business) workspace. Async: awaited by the briefing hook.
 * The caller only invokes this for business workspaces.
 */
export async function getTeamFlags(currentUser, tasks) {
  const isTeamPlan = userMeetsPlan(currentUser, 'team') ||
    ['admin', 'super_admin'].includes(currentUser?.role); // FIX-3 (admin escape hatch preserved)
  if (!isTeamPlan) return [];

  let members = [];
  try {
    const res = await api('/api/team/members');
    if (res?.ok) {
      const data = await res.json();
      members = Array.isArray(data.members) ? data.members : [];
    }
  } catch {
    return [];
  }
  if (!members.length) return [];
  const memberById = new Map(members.map(m => [m.id, m]));

  const now = new Date();
  const flags = [];

  tasks.forEach(task => {
    if (task.completed || !task.assignedTeam?.length) return;
    const assignedAt = new Date(task.createdAt || task.assignedAt || task.datetime);
    if (isNaN(assignedAt)) return;
    if ((now - assignedAt) / 3600000 < 48) return;

    task.assignedTeam.forEach(memberId => {
      if (memberId === currentUser?.id) return;
      const member = memberById.get(memberId);
      if (!member) return;
      flags.push({
        memberName:  member.name,
        taskName:    task.title,
        assignedDate: assignedAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      });
    });
  });

  return flags.slice(0, 5);
}

const NO_MARKDOWN_INSTRUCTION =
  'Never use markdown syntax in your response. No hashtags, no asterisks, no hyphens as bullet points, no heading syntax. ' +
  'Write in clean flowing prose only. Do not start with a title or heading — the date and greeting are already shown separately in the UI. ' +
  'Write as a warm, professional human assistant would speak — natural sentences and paragraphs only.';

const LANG_INSTRUCTIONS = {
  'en-GB': 'Use English (UK) spelling and phrasing.',
  'en-US': 'Use American English spelling and phrasing.',
  'en-AU': 'Use Australian English spelling and phrasing.',
  'en-CA': 'Use Canadian English spelling and phrasing.',
  'fr-FR': 'Write entirely in French (France).',
  'fr-CA': 'Write entirely in Canadian French.',
  'de-DE': 'Write entirely in German.',
  'es-ES': 'Write entirely in Spanish (Spain).',
  'it-IT': 'Write entirely in Italian.',
  'nl-NL': 'Write entirely in Dutch.',
};

function langInstruction(lang) {
  return LANG_INSTRUCTIONS[lang] || LANG_INSTRUCTIONS['en-GB'];
}

/** Call /api/aidey with a system prompt and user payload. Returns reply text or null. */
async function callAidey(systemPrompt, payload) {
  try {
    const res = await api('/api/aidey', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        system: systemPrompt,
        messages: [{ role: 'user', content: JSON.stringify(payload) }],
      }),
    });
    if (!res?.ok) return null;
    const data = await res.json();
    return data.reply?.trim() || null;
  } catch {
    return null;
  }
}

/**
 * Generate a 3-sentence morning summary.
 */
export async function generateAISummary(briefingData, lang = 'en-GB') {
  const lowCount = briefingData.lowPriorityNotificationCount || 0;
  return callAidey(
    'You are Aidey, a professional executive assistant. ' +
    `Language: ${langInstruction(lang)} ` +
    'Start your response with a single punchy summary sentence (no more than 20 words) that captures the day at a glance, followed by a blank line, then your full briefing text (max 3 sentences). ' +
    'Example first line: "Busy morning ahead — 3 meetings, 5 priority tasks and 2 overdue invoices." ' +
    (lowCount > 0 ? `If there are background updates, mention them briefly: "You also have ${lowCount} background update${lowCount > 1 ? 's' : ''} since yesterday." ` : '') +
    'If detectedEvents > 0, mention them briefly, e.g. "I also spotted 2 events in your inbox that need confirming." ' +
    'Do not use corporate jargon. Do not say "I hope you have a great day". ' +
    'Sound like a smart colleague, not a chatbot. ' +
    NO_MARKDOWN_INSTRUCTION,
    {
      meetings:     briefingData.meetings.length,
      meetingNames: briefingData.meetings.map(m => m.title),
      tasks:        briefingData.priorityTasks.length,
      overdue:      briefingData.priorityTasks.filter(t => t.daysOverdue > 0).length,
      invoices:     briefingData.invoiceAlerts.length,
      teamFlags:    briefingData.teamFlags.length,
      detectedEvents: (briefingData.eventProposals || []).length,
      weather:      briefingData.weather,
      date:         briefingData.greeting.dateStr,
      backgroundUpdates: lowCount,
    }
  );
}

/**
 * Generate a 2–3 sentence afternoon check-in summary.
 */
export async function generateAfternoonAISummary(briefingData, lang = 'en-GB') {
  return callAidey(
    'You are Aidey, a professional executive assistant. ' +
    `Language: ${langInstruction(lang)} ` +
    'Start your response with a single punchy summary sentence (no more than 20 words) that captures the afternoon at a glance, followed by a blank line, then your full check-in text (max 2–3 sentences). ' +
    'Example first line: "Afternoon mostly on track — 3 of 5 tasks done and 2 meetings still ahead." ' +
    'Acknowledge what has been accomplished, flag what still needs attention, and motivate for the rest of the day. ' +
    'If detectedEvents > 0, mention them briefly (events spotted in the inbox that need confirming). ' +
    'Sound human and direct — not a chatbot. ' +
    NO_MARKDOWN_INSTRUCTION,
    {
      completedCount:   briefingData.completedToday.length,
      outstandingCount: briefingData.outstandingToday.length,
      outstandingTasks: briefingData.outstandingToday.map(t => t.title),
      remainingMeetings: briefingData.remainingMeetings.length,
      overdueFollowUps:  briefingData.overdueFollowUps.length,
      tomorrowMeetings:  briefingData.tomorrowMeetings.length,
      detectedEvents:    (briefingData.eventProposals || []).length,
      date: briefingData.greeting.dateStr,
    }
  );
}

/**
 * Generate a 3-sentence evening wrap-up with a closing motivational line.
 */
export async function generateEveningAISummary(briefingData, lang = 'en-GB') {
  return callAidey(
    'You are Aidey, a professional executive assistant. ' +
    `Language: ${langInstruction(lang)} ` +
    'Start your response with a single punchy summary sentence (no more than 20 words) that captures the day at a glance, followed by a blank line, then your full wrap-up (max 3 sentences). ' +
    'Example first line: "Solid day — 6 tasks done, 2 rolled to tomorrow." ' +
    'Reflect on what was accomplished, acknowledge what rolls over, and close with a short motivational line (one sentence). ' +
    'If detectedEvents > 0, mention them briefly (events spotted in the inbox still to confirm). ' +
    'Tone: warm, human, and encouraging — not corporate. ' +
    NO_MARKDOWN_INSTRUCTION,
    {
      completedCount:  briefingData.completedToday.length,
      completedTasks:  briefingData.completedToday.map(t => t.title),
      rolledOverCount: briefingData.rolledOver.length,
      totalPlanned:    briefingData.totalPlanned,
      firstTomorrow:   briefingData.firstTomorrowMeeting?.title || null,
      invoices:        briefingData.invoiceAlerts.length,
      detectedEvents:  (briefingData.eventProposals || []).length,
      date:            briefingData.greeting.dateStr,
    }
  );
}

/**
 * Assemble all synchronous briefing data into one object.
 * Weather and AI summary are filled in asynchronously by the hook.
 */
export function buildBriefingData(currentUser, tasks, googleEvents, icalEvents, outlookEvents, allUsers, invoices = [], workspaceType = null) {
  const now      = new Date();
  const userLocale = currentUser?.language || 'en-GB';
  const dateStr  = now.toLocaleDateString(userLocale, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const firstName = currentUser?.firstName
    || currentUser?.name?.split(' ')[0]
    || 'there';

  return {
    date:          now.toDateString(),
    greeting:      { name: firstName, dateStr },
    meetings:      getTodaysMeetings(tasks, googleEvents, icalEvents, outlookEvents),
    priorityTasks: getPriorityTasks(tasks),
    // Invoices are a business-only concept — no invoice section in personal/family briefings.
    invoiceAlerts: workspaceType === 'business' ? getInvoiceAlerts(invoices) : [],
    teamFlags:     [], // filled async (getTeamFlags), business workspaces only
    eventProposals: [], // filled async (fetchEventProposals)
    weather:       null, // filled async
    aiSummary:     null, // filled async
  };
}

// ── helpers ───────────────────────────────────────────────────────────────────
function formatTime(dt) {
  try {
    return new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function firstName(user) {
  return user?.firstName || user?.name?.split(' ')[0] || 'there';
}

function todayDateStr() {
  return new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

// ── Afternoon data helpers ────────────────────────────────────────────────────

/** Tasks marked complete that were due today. */
export function getCompletedTodayTasks(tasks) {
  const todayStr = new Date().toDateString();
  return tasks
    .filter(t => {
      if (!t.completed) return false;
      const dt = new Date(t.datetime || t.date);
      return !isNaN(dt) && dt.toDateString() === todayStr;
    })
    .map(t => ({ title: t.title }));
}

/** Non-completed tasks still due today, time-sorted. */
export function getOutstandingTodayTasks(tasks) {
  const todayStr = new Date().toDateString();
  return tasks
    .filter(t => {
      if (t.completed) return false;
      const dt = new Date(t.deadline || t.datetime);
      return !isNaN(dt) && dt.toDateString() === todayStr;
    })
    .sort((a, b) => new Date(a.deadline || a.datetime) - new Date(b.deadline || b.datetime))
    .slice(0, 6)
    .map(t => ({
      title: t.title,
      priority: t.priority || 'Medium',
      time: formatTime(t.datetime),
    }));
}

/** Meetings (calendar + task) that start after right now. */
export function getRemainingMeetingsToday(tasks, googleEvents = [], icalEvents = [], outlookEvents = []) {
  const now = new Date();
  const todayStr = now.toDateString();

  const calEvents = [...googleEvents, ...icalEvents, ...outlookEvents]
    .map(e => ({
      time: e.time || formatTime(e.start || e.datetime),
      title: e.summary || e.title || e.subject || 'Event',
      location: e.location || e.meetingLink || '',
      _dt: new Date(e.start || e.datetime || e.date),
    }))
    .filter(e => !isNaN(e._dt) && e._dt.toDateString() === todayStr && e._dt > now);

  const meetTasks = tasks
    .filter(t => !t.completed && t.taskType === 'meeting')
    .filter(t => {
      const dt = new Date(t.datetime);
      return !isNaN(dt) && dt.toDateString() === todayStr && dt > now;
    })
    .map(t => ({
      time: formatTime(t.datetime),
      title: t.title,
      location: t.location || '',
      _dt: new Date(t.datetime),
    }));

  return [...calEvents, ...meetTasks]
    .sort((a, b) => a._dt - b._dt)
    .map(({ _dt, ...rest }) => rest);
}

/** All meetings tomorrow, sorted by time. */
export function getTomorrowsMeetings(tasks, googleEvents = [], icalEvents = [], outlookEvents = []) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toDateString();

  const calEvents = [...googleEvents, ...icalEvents, ...outlookEvents]
    .map(e => ({
      time: e.time || formatTime(e.start || e.datetime),
      title: e.summary || e.title || e.subject || 'Event',
      location: e.location || '',
      _dt: new Date(e.start || e.datetime || e.date),
    }))
    .filter(e => !isNaN(e._dt) && e._dt.toDateString() === tomorrowStr);

  const meetTasks = tasks
    .filter(t => !t.completed && t.taskType === 'meeting')
    .filter(t => {
      const dt = new Date(t.datetime);
      return !isNaN(dt) && dt.toDateString() === tomorrowStr;
    })
    .map(t => ({
      time: formatTime(t.datetime),
      title: t.title,
      location: t.location || '',
      _dt: new Date(t.datetime),
    }));

  return [...calEvents, ...meetTasks]
    .sort((a, b) => a._dt - b._dt)
    .map(({ _dt, ...rest }) => rest);
}

// ── Afternoon Check-in builder ────────────────────────────────────────────────

export function buildAfternoonCheckInData(currentUser, tasks, googleEvents, icalEvents, outlookEvents, _allUsers, invoices = []) {
  const now = new Date();
  return {
    mode:             'afternoon',
    date:             now.toDateString(),
    greeting:         { name: firstName(currentUser), dateStr: todayDateStr() },
    completedToday:   getCompletedTodayTasks(tasks),
    outstandingToday: getOutstandingTodayTasks(tasks),
    remainingMeetings: getRemainingMeetingsToday(tasks, googleEvents, icalEvents, outlookEvents),
    overdueFollowUps: getPriorityTasks(tasks).filter(t => t.daysOverdue > 0),
    tomorrowMeetings: getTomorrowsMeetings(tasks, googleEvents, icalEvents, outlookEvents).slice(0, 3),
    eventProposals:   [], // filled async (fetchEventProposals)
    aiSummary:        null,
  };
}

// ── Evening Wrap-up builder ───────────────────────────────────────────────────

export function buildEveningWrapUpData(currentUser, tasks, googleEvents, icalEvents, outlookEvents, _allUsers, invoices = [], workspaceType = null) {
  const now = new Date();
  const todayStr = now.toDateString();

  const allTodayTasks = tasks.filter(t => {
    const dt = new Date(t.deadline || t.datetime);
    return !isNaN(dt) && dt.toDateString() === todayStr;
  });

  const tomorrowMeetings = getTomorrowsMeetings(tasks, googleEvents, icalEvents, outlookEvents);

  return {
    mode:                'evening',
    date:                now.toDateString(),
    greeting:            { name: firstName(currentUser), dateStr: todayDateStr() },
    completedToday:      allTodayTasks.filter(t => t.completed).map(t => ({ title: t.title })),
    rolledOver:          allTodayTasks.filter(t => !t.completed).map(t => ({ title: t.title, priority: t.priority || 'Medium' })),
    totalPlanned:        allTodayTasks.length,
    firstTomorrowMeeting: tomorrowMeetings[0] || null,
    tomorrowMeetings,
    // Invoices are a business-only concept — no invoice section in personal/family briefings.
    invoiceAlerts:       workspaceType === 'business' ? getInvoiceAlerts(invoices) : [],
    eventProposals:      [], // filled async (fetchEventProposals)
    aiSummary:           null,
  };
}
